import useFilter from "@hooks/useFilter";
import { styles } from "@components/styles";
import { Collapse } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { constants } from "../context/constants";
import { TriangleUpIcon, WateringCanIcon } from "@components/icons";
import { DataScienceContext } from "../context/DataScienceContext";
import React, { useState, useContext, useEffect, useMemo } from "react";

function TreeList({ project }) {
  const {
    filterState,
    filterDispatch,
    treeLineColor,
    setActiveHealthData,
    setActiveIrrigationData,
  } = useContext(DataScienceContext);
  const {
    trees,
    areasId,
    treesId,
    installationsId,
    installations,
    healthTreeId,
    irrigationTreeId,
  } = filterState;
  const [filterTerm, setFilterTerm] = useState("");
  const [open, setOpen] = useState(true);
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const { t } = useTranslation();

  const filteredTrees = useMemo(
    () => useFilter({ filterTerm, filterItem: trees }),
    [filterTerm, trees]
  );

  const shouldFetch = !project || !project.areas;

  useEffect(() => {
    filterDispatch({ type: constants.RESET_TREES, payload: [] });
    if (!shouldFetch && project?.areas) {
      project.areas
        .filter((area) => areasId.includes(area.id))
        .forEach((area) => {
          if (area.tree_set?.length) {
            filterDispatch({
              type: constants.SET_TREES,
              payload: area.tree_set,
            });
          }
        });
    }
  }, [shouldFetch, project, areasId, filterDispatch]);

  useEffect(() => {
    if (healthTreeId != null && !treesId.includes(healthTreeId)) {
      filterDispatch({ type: constants.SET_HEALTH_TREE, payload: null });
      setActiveHealthData(false);
    }
    if (irrigationTreeId != null && !treesId.includes(irrigationTreeId)) {
      filterDispatch({ type: constants.SET_IRRIGATION_TREE, payload: null });
      setActiveIrrigationData(false);
    }
  }, [
    treesId,
    healthTreeId,
    irrigationTreeId,
    filterDispatch,
    setActiveHealthData,
    setActiveIrrigationData,
  ]);

  useEffect(() => {
    // Update the select all checkbox based on whether any trees are initially selected
    setSelectAllChecked(
      treesId.length > 0 && filteredTrees.every((tree) => treesId.includes(tree.id))
    );
  }, [filteredTrees, treesId]);

  function updateFilterHandler(event) {
    setFilterTerm(event.target.value);
  }

  //5. Check and uncheck functionalities to set and filter tree from tree list
  const handleChange = (props) => {
    const { e, tree } = props;
    const isChecked = e.target.checked;
    const treeInstallations = tree.installations;

    // Determine new list of checked trees based on current action
    const newCheckedTrees = isChecked
      ? [...treesId, tree.id] // Add tree id if checked
      : treesId.filter((id) => id !== tree.id); // Remove tree id if unchecked

    if (isChecked) {
      filterDispatch({ type: constants.SET_CHECKED_TREES, payload: [tree.id] });
      const ilist = (tree.installations || []).map((inst) => inst.id);
      const filteredInstallation = installationsId.filter((f) => ilist.includes(f));
      filterDispatch({
        type: constants.SET_CHECKED_INSTALLATIONS,
        payload: filteredInstallation,
      });
    } else {
      if (healthTreeId === tree.id) {
        filterDispatch({ type: constants.SET_HEALTH_TREE, payload: null });
        setActiveHealthData(false);
      }
      if (irrigationTreeId === tree.id) {
        filterDispatch({ type: constants.SET_IRRIGATION_TREE, payload: null });
        setActiveIrrigationData(false);
      }
      const filteredTree = Array.from(new Set(treesId.filter((id) => id !== tree.id)));
      filterDispatch({ type: constants.RESET_CHECKED_TREES, payload: filteredTree });
      const treeInstallationsId = treeInstallations.map((installation) => installation.id);
      const filteredInstallationsId = installationsId.filter(
        (installationId) => !treeInstallationsId.includes(installationId)
      );
      const filteredInstallations = installations.filter(
        (installation) => !treeInstallationsId.includes(installation.id)
      );
      filterDispatch({
        type: constants.RESET_CHECKED_INSTALLATIONS,
        payload: filteredInstallationsId,
      });
      filterDispatch({
        type: constants.RESET_INSTALLATIONS,
        payload: filteredInstallations,
      });
    }
    // Update Select All Checkbox State
    // Check if all trees are now checked or not
    const allChecked = filteredTrees.every((tree) => newCheckedTrees.includes(tree.id));
    setSelectAllChecked(allChecked);
  };

  // 6.handle all select checkbox
  const handleSelectAll = (props) => {
    const { e, filteredTrees } = props;
    const isChecked = e.target.checked;

    // Set the checked state for all or none
    setSelectAllChecked(isChecked);

    if (isChecked) {
      const ids = filteredTrees.map((tree) => tree.id);
      filterDispatch({ type: constants.SET_CHECKED_TREES, payload: [ids] });
      // TODO: inconsistency here: installations should be set
    } else {
      filterDispatch({ type: constants.RESET_CHECKED_TREES, payload: [] });
      filterDispatch({ type: constants.RESET_CHECKED_INSTALLATIONS, payload: [] });
      filterDispatch({ type: constants.SET_HEALTH_TREE, payload: null });
      filterDispatch({ type: constants.SET_IRRIGATION_TREE, payload: null });
      setActiveHealthData(false);
      setActiveIrrigationData(false);
    }
  };

  // 7. Handle health selection for tree
  const handleHealthSelection = (treeId) => {
    const newHealthTreeId = healthTreeId === treeId ? null : treeId;
    filterDispatch({ type: constants.SET_HEALTH_TREE, payload: newHealthTreeId });

    // Set health state (but don't trigger fetch - that happens on loadData button click)
    if (newHealthTreeId !== null) {
      setActiveHealthData(true);
    } else {
      setActiveHealthData(false);
    }
  };

  const handleIrrigationSelection = (treeId) => {
    const newIrrigationTreeId = irrigationTreeId === treeId ? null : treeId;
    filterDispatch({ type: constants.SET_IRRIGATION_TREE, payload: newIrrigationTreeId });

    if (newIrrigationTreeId !== null) {
      setActiveIrrigationData(true);
    } else {
      setActiveIrrigationData(false);
    }
  };

  return (
    <div className="me-3" data-tour="ds-filter-trees">
      <div className="d-flex pe-2 justify-content-between">
        <div
          className={styles.dsFilterTitle}
          onClick={() => setOpen(!open)}
          aria-controls="example-collapse-text"
          aria-expanded={open}
        >
          <TriangleUpIcon open={open} /> <span className=" ds-filter-title">{t("trees")}</span>
        </div>
        <div>
          <input
            type="checkbox"
            checked={selectAllChecked}
            onChange={(e) => handleSelectAll({ e, filteredTrees })}
          />
        </div>
      </div>
      <Collapse in={open}>
        <div>
          <div className="py-2 pe-2">
            <input
              type="text"
              className="form-control filter-search"
              placeholder={t("search_tree")}
              onChange={updateFilterHandler}
            />
          </div>
          <ul className="p-0 pe-2  filter__list" id="example-collapse-text">
            {filteredTrees?.map((tree, i) => (
              <li
                key={i}
                className={`filter__items  gap-1 d-flex justify-content-between align-items-center ps-1`}
              >
                <span>{tree.name}</span>
                <div className="d-flex gap-2 align-items-center">
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => handleHealthSelection(tree.id)}
                    title={
                      healthTreeId === tree.id
                        ? "Health monitoring active for this tree"
                        : "Monitor health for this tree"
                    }
                    style={{
                      fontSize: "12px",
                      padding: "2px 8px",
                      minWidth: "auto",
                      border: "none",
                      backgroundColor: healthTreeId === tree.id ? "#a8a9aa" : "transparent",
                      color: "#333",
                    }}
                  >
                    {healthTreeId === tree.id ? "❤️" : "🫀"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => handleIrrigationSelection(tree.id)}
                    title={
                      irrigationTreeId === tree.id
                        ? t("irrigation") + " active for this tree"
                        : t("irrigation") + " for this tree"
                    }
                    style={{
                      fontSize: "12px",
                      padding: "2px 8px",
                      minWidth: "auto",
                      border: "none",
                      backgroundColor: irrigationTreeId === tree.id ? "#a8a9aa" : "transparent",
                      color: "#0d6efd",
                    }}
                  >
                    <WateringCanIcon size="16px" />
                  </button>
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      handleChange({ e, tree });
                    }}
                    style={{
                      background: treesId.includes(tree.id)
                        ? treeLineColor[tree.id] || "var(--bs-primary, #0d6efd)"
                        : "var(--color-bg-surface, #ffffff)",
                      border: treesId.includes(tree.id)
                        ? `2px solid ${treeLineColor[tree.id] || "var(--bs-primary, #0d6efd)"}`
                        : "1px solid var(--color-border-strong, #888888)",
                      outline: "none",
                      cursor: "pointer",
                    }}
                    checked={treesId.includes(tree.id)}
                  />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Collapse>
    </div>
  );
}

export default React.memo(TreeList);
